/**
 * Open/close state for the read-only preview modal — editor UI state, not document state. It
 * touches no store, and the preview below stays as write-free as it was in the column (§2-1).
 *
 * The flag is MODULE-SCOPED because two unrelated components share it: the page opens it, and
 * `ModalAssessmentPreview` reads and clears it. Every other modal here gets that sharing from
 * Pinia; with a store ruled out, a module ref is the same singleton at a smaller radius, and it
 * keeps the modal component prop-free.
 *
 * Module scope outlives the page, so the flag is cleared on scope dispose. There is deliberately
 * NO SINGLE OWNER — every call site inside an effect scope registers its own dispose and sets the
 * same flag to `false`, which is idempotent by construction.
 *
 * Dispose alone is not enough: Vue Router reuses the editor instance when only `[id]` changes, so
 * the Duplicate path unmounts nothing. `assessment-editor.hook.ts`'s `watch(templateId, …)` — the
 * one place that already knows the document is being re-seated — calls `closePreview()` there.
 */
import { computed, getCurrentScope, onScopeDispose, ref } from 'vue'

import type { ComputedRef } from 'vue'

const isOpen = ref<boolean>(false)

export interface UseAssessmentPreviewModalReturn {
  refIsOpen: ComputedRef<boolean>
  openPreview: () => void
  closePreview: () => void
}

export const useAssessmentPreviewModal = (): UseAssessmentPreviewModalReturn => {
  const refIsOpen = computed<boolean>(() => isOpen.value)

  const openPreview = (): void => {
    isOpen.value = true
  }

  const closePreview = (): void => {
    isOpen.value = false
  }

  // Guarded so the hook stays callable outside a component — a unit test must not trip
  // `onScopeDispose`'s "no active effect scope" warning.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      isOpen.value = false
    })
  }

  return {
    refIsOpen,
    openPreview,
    closePreview,
  }
}
